'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  X,
  Loader2,
  Sparkles,
  ExternalLink,
  CalendarCheck,
  Mail,
  ArrowRight,
} from 'lucide-react'
import type { ToolKey, ToolState, Insight, ActionItem } from '@/types/radar'
import { sanitizeHref } from '@/lib/sanitize'
import { Button, IconButton, Input } from '@/components/platform/ui'
import AskShell, {
  useRotatingPlaceholder,
  type AskShellHandle,
  type AskShellMode,
} from '@/components/chat/AskShell'
import { usePrefersReducedMotion } from '@/components/chat/useVectorChat'
import { radarAnalytics } from '@/lib/analytics/radar-events'
import {
  useAdvisorChat,
  FREE_TIER_MESSAGE_CAP,
  type AdvisorMessage,
} from './useAdvisorChat'

interface AdvisorChatProps {
  domain: string | null
  auditContext: string
  toolResults?: Record<ToolKey, ToolState>
  insights?: Insight[]
  actions?: ActionItem[]
  suggestedQuestions?: string[]
  userTier?: 'free' | 'paid'
  onUpgradeClick?: () => void
  /** Opens the chat + auto-sends a question (from the inline entry card). Bump nonce to re-fire. */
  externalTrigger?: { question: string; nonce: number } | null
}

const MINIMIZED_KEY = 'pm_strategist_minimized'

const DEFAULT_QUESTIONS = [
  'How do I improve my AI visibility score?',
  'What is GEO and how does it help?',
  'Why are AI bots blocked on my site?',
  'How should I structure my llms.txt?',
  'What drives AI citation rates?',
]

/**
 * AdvisorChat — the Readiness Strategist's content + business logic rendered
 * through the shared AskShell, so it matches Vector's presentation exactly.
 * All revenue/lead logic (free-tier cap, upgrade gate, email capture, soft
 * CTA) lives here and in useAdvisorChat; layout and states live in AskShell.
 * Stays at z-50 (both layers) because the page's upgrade modal renders later
 * in the DOM at z-50 and must stack above the gate.
 */
export default function AdvisorChat({
  domain,
  auditContext,
  toolResults,
  insights,
  actions,
  suggestedQuestions,
  userTier = 'free',
  onUpgradeClick,
  externalTrigger,
}: AdvisorChatProps) {
  const [emailInput, setEmailInput] = useState('')
  const [shellMode, setShellMode] = useState<AskShellMode>('initial')
  // Typewriter waits for the shell's on-load entrance to settle
  const [introDone, setIntroDone] = useState(false)
  const shellRef = useRef<AskShellHandle>(null)
  const lastTriggerNonce = useRef<number | null>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const handleIntroComplete = useCallback(() => setIntroDone(true), [])

  const {
    messages,
    sendMessage,
    skipTypewriter,
    loading,
    typingId,
    isGated,
    showSoftCTA,
    dismissCta,
    showEmailCapture,
    hideEmailCapture,
    submitEmail,
    emailSubmitting,
    emailSubmitted,
  } = useAdvisorChat({
    domain,
    auditContext,
    toolResults,
    insights,
    actions,
    userTier,
  })

  // Use dynamic questions if available, fall back to defaults
  const activeQuestions = useMemo(
    () =>
      suggestedQuestions && suggestedQuestions.length > 0
        ? suggestedQuestions
        : DEFAULT_QUESTIONS,
    [suggestedQuestions]
  )

  // radar_advisor_opened: first strategist interaction of this mount
  // (outcome event, not a raw open click). Entry point distinguishes the
  // floating shell input from the inline entry card.
  const advisorOpenedRef = useRef(false)
  const markAdvisorOpened = (entryPoint: 'fab' | 'entry_card') => {
    if (advisorOpenedRef.current) return
    advisorOpenedRef.current = true
    radarAnalytics.advisorOpened(entryPoint)
  }

  // radar_advisor_limit_reached: only on the in-session transition to
  // gated (seeded with the mount state so returning capped users don't
  // re-fire it on every visit).
  const prevGatedRef = useRef(isGated)
  useEffect(() => {
    if (isGated && !prevGatedRef.current) {
      radarAnalytics.advisorLimitReached()
    }
    prevGatedRef.current = isGated
  }, [isGated])

  // External trigger from the inline Readiness Strategist entry card: open the
  // chat and auto-send the chosen question. The free-tier cap is never bypassed
  // — when gated we open and surface the upgrade path instead of sending.
  useEffect(() => {
    if (!externalTrigger) return
    if (lastTriggerNonce.current === externalTrigger.nonce) return
    lastTriggerNonce.current = externalTrigger.nonce
    if (isGated) {
      shellRef.current?.restore()
      onUpgradeClick?.()
      return
    }
    if (externalTrigger.question.trim()) {
      markAdvisorOpened('entry_card')
      shellRef.current?.openAnswer()
      void sendMessage(externalTrigger.question)
    } else {
      shellRef.current?.restore()
    }
  }, [externalTrigger, isGated, sendMessage, onUpgradeClick])

  const initiallyMinimized =
    typeof window !== 'undefined' &&
    // 'true' = pill, 'docked' = wide docked bar; the shell reads which
    sessionStorage.getItem(MINIMIZED_KEY) !== null

  // Looping typewriter placeholder over the (audit-personalized) questions,
  // matching Vector's initial-state behavior exactly.
  const placeholder = useRotatingPlaceholder(
    activeQuestions,
    shellMode === 'initial' && introDone,
    prefersReducedMotion
  )

  const totalContentLength = messages.reduce((n, m) => n + m.content.length, 0)

  return (
    <AskShell
      ref={shellRef}
      isVisible
      title='Ask the Strategist'
      subtitle='Briefed on your audit'
      initialPlaceholder={`Try asking: ${placeholder}`}
      suggestionsPlaceholder='Ask about AI visibility...'
      answerPlaceholder={
        isGated ? 'Free message limit reached' : 'Ask another question'
      }
      inputAriaLabel='Ask the Strategist a question'
      suggestions={activeQuestions}
      suggestionsHeader={
        <p className='mb-1 px-1 text-xs text-muted-foreground'>
          Ask anything about AI visibility strategy. I use Pixelmojo&apos;s
          knowledge base, industry GEO/AEO methodologies, and your audit data
          {userTier === 'free'
            ? ` to give tailored advice. ${FREE_TIER_MESSAGE_CAP} free messages.`
            : ' to give tailored advice.'}
        </p>
      }
      canSubmit={!loading && !isGated}
      inputDisabled={isGated}
      onSubmit={question => {
        markAdvisorOpened('fab')
        void sendMessage(question)
      }}
      hasConversation={messages.length > 0}
      storageKey={MINIMIZED_KEY}
      initiallyMinimized={initiallyMinimized}
      onModeChange={setShellMode}
      onIntroComplete={handleIntroComplete}
      dataTour='advisor'
      overlayZClass='z-50'
      backdropZClass='z-50'
      answerFooter={
        <div className='border-t border-border px-4 py-2.5'>
          <p className='text-center text-xs text-muted-foreground'>
            Powered by Vector + Hive
          </p>
        </div>
      }
      scrollKey={`${messages.length}|${totalContentLength}|${loading}`}
    >
      {messages.length === 0 && !loading && (
        <p className='text-sm text-muted-foreground'>
          Ask anything about AI visibility strategy. I use Pixelmojo&apos;s
          knowledge base, industry GEO/AEO methodologies, and your audit data to
          give tailored advice.
        </p>
      )}

      {messages.map(msg =>
        msg.role === 'user' ? (
          <div key={msg.id}>
            <span className='text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>
              You asked
            </span>
            <p className='mt-1 text-base font-semibold leading-snug text-foreground md:text-lg'>
              {msg.content}
            </p>
          </div>
        ) : (
          <AdvisorAnswer
            key={msg.id}
            message={msg}
            isTyping={msg.id === typingId}
            onSkip={skipTypewriter}
          />
        )
      )}

      {loading && typingId === null && (
        <div className='flex items-center gap-2 text-muted-foreground'>
          <Loader2
            size={14}
            className='animate-spin text-primary motion-reduce:animate-none'
          />
          <span className='text-sm'>Thinking...</span>
        </div>
      )}

      {/* Soft CTA banner (messages 6-10) */}
      {showSoftCTA && !isGated && typingId === null && (
        <div className='relative rounded-xl border border-primary/20 bg-primary/5 px-4 py-3'>
          <IconButton
            icon={X}
            aria-label='Dismiss'
            variant='ghost'
            size='xs'
            rounded='md'
            onClick={dismissCta}
            className='absolute right-1.5 top-1.5'
          />
          <p className='pr-6 text-xs leading-relaxed text-muted-foreground'>
            Finding this helpful? Get hands-on implementation support.
          </p>
          <div className='mt-2 flex gap-2'>
            <a
              href='https://www.pixelmojo.io/services/ai-visibility-strategy'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1.5 rounded-lg bg-[var(--primary-fill)] px-2.5 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-[#006ba1]'
            >
              <CalendarCheck size={12} /> Strategy session
            </a>
            <a
              href='https://www.pixelmojo.io/platform'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
            >
              <ArrowRight size={12} /> Explore Radar Pro
            </a>
          </div>
        </div>
      )}

      {/* Email capture (triggered by intent signals) */}
      {showEmailCapture && !emailSubmitted && typingId === null && (
        <div className='space-y-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3'>
          <p className='text-xs text-foreground'>
            It sounds like you could use some hands-on help. Want our team to
            review your audit and reach out with specific recommendations?
          </p>
          <div className='flex gap-2'>
            <Input
              srLabel='Your email'
              type='email'
              inputSize='sm'
              rounded='md'
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder='Your email'
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void submitEmail(emailInput)
                }
              }}
              disabled={emailSubmitting}
              containerClassName='flex-1'
            />
            <Button
              variant='primary'
              size='sm'
              rounded='md'
              loading={emailSubmitting}
              loadingText=''
              onClick={() => void submitEmail(emailInput)}
              disabled={!emailInput.trim()}
            >
              Send
            </Button>
          </div>
          <button
            onClick={hideEmailCapture}
            className='text-[10px] text-muted-foreground hover:underline'
          >
            No thanks, I will handle it myself
          </button>
        </div>
      )}

      {/* Engagement gate (free-tier cap) */}
      {isGated && typingId === null && (
        <div className='space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4'>
          <p className='text-xs font-medium text-foreground'>
            You have used your {FREE_TIER_MESSAGE_CAP} free Readiness Strategist
            messages.
          </p>
          <p className='text-[11px] text-muted-foreground'>
            Unlock unlimited messages with any paid plan — from $5.
          </p>
          <div className='max-w-md space-y-2'>
            <Button
              variant='gradient'
              size='sm'
              rounded='md'
              fullWidth
              leadingIcon={Sparkles}
              onClick={() => onUpgradeClick?.()}
            >
              Unlock unlimited strategist
            </Button>
            <a
              href='mailto:founders@pixelmojo.io'
              className='flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-[11px] text-muted-foreground transition-colors hover:text-foreground'
            >
              <Mail size={12} />
              Email founders@pixelmojo.io
            </a>
          </div>
        </div>
      )}
    </AskShell>
  )
}

function AdvisorAnswer({
  message,
  isTyping,
  onSkip,
}: {
  message: AdvisorMessage
  isTyping: boolean
  onSkip: () => void
}) {
  return (
    <div>
      <p className='whitespace-pre-wrap text-sm leading-relaxed text-foreground md:text-base'>
        {message.content}
        {isTyping && (
          <span className='ml-0.5 inline-block h-3 w-0.5 animate-pulse bg-primary motion-reduce:animate-none' />
        )}
      </p>
      {/* Skip affordance — only on the actively-typing message, flushes the
          rest of the answer immediately (eval P3 #19 interruptible). */}
      {isTyping && (
        <button
          type='button'
          onClick={onSkip}
          className='mt-1.5 text-[10px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background'
        >
          Skip animation
        </button>
      )}
      {/* Show related entities only after typing is done */}
      {message.relatedEntities &&
        message.relatedEntities.length > 0 &&
        !isTyping && (
          <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-border/40 pt-2'>
            {message.relatedEntities.map((entity, idx) => {
              const safeHref = sanitizeHref(entity.url)
              if (!safeHref) return null
              return (
                <a
                  key={`entity-${idx}`}
                  href={safeHref}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex items-center gap-1 text-[11px] text-primary hover:underline'
                >
                  <ExternalLink size={12} /> {entity.name}
                </a>
              )
            })}
          </div>
        )}
    </div>
  )
}
